import { useEffect, useRef, useState } from 'react'
import { CHANGELOG, type ChangelogEntry } from '@/changelog'
import { APP_VERSION } from '@/version'
import { authApi } from '@/api/auth'
import { filtrarEntradas, type EntradaFiltrada } from '@/lib/novidades/filtro'

/** Entradas do changelog mais novas que `lastSeen` (por índice do array, que já é a ordem canônica). */
export function computeChangelogDelta(changelog: ChangelogEntry[], lastSeen: string | null | undefined): ChangelogEntry[] {
  if (!changelog.length) return []
  if (!lastSeen) return [changelog[0]]
  const idx = changelog.findIndex((e) => e.version === lastSeen)
  if (idx === -1) return [changelog[0]]  // versão vista não está mais no array
  if (idx === 0) return []               // já viu a mais recente
  return changelog.slice(0, idx)
}

/** Total de itens (novidades + correções + melhorias) das entradas mostradas. */
export function contarItens(entries: ChangelogEntry[]): number {
  return entries.reduce((n, e) => n + e.features.length + e.fixes.length + e.improvements.length, 0)
}

/** Teto de `duracao_ms` aceito por `POST /me/changelog-seen` (`le=86_400_000`, 24 h).
 * Acima disso o servidor devolve 422 e a versão não seria marcada como vista. */
export const DURACAO_MAXIMA_MS = 86_400_000

/** `a` comparada com `b` por número (X.Y.Z): negativo, zero ou positivo. */
export function compararVersao(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0)
    if (d !== 0) return d
  }
  return 0
}

/** Onde a versão marcada como vista fica para as OUTRAS abas do mesmo navegador. Por
 * usuário: outro login no mesmo navegador não herda o que esta pessoa viu. */
export const chaveDoVisto = (userId: number) => `consoleai:changelog-visto:${userId}`

function lerVisto(chave: string | null): string | null {
  if (!chave) return null
  try { return localStorage.getItem(chave) } catch { return null }  // modo restrito: sem coordenação
}

function gravarVisto(chave: string | null, versao: string) {
  if (!chave) return
  try { localStorage.setItem(chave, versao) } catch { /* modo restrito: sem coordenação */ }
}

/**
 * A versão que OUTRA aba deste navegador já marcou como vista, ao vivo. O `user` de cada
 * aba só é relido do servidor no carregamento: sem isto, fechar o aviso numa aba deixava
 * as outras com o aviso aberto, e cada uma gravava a sua marcação (#25905). O evento
 * `storage` só dispara nas outras abas; ao voltar a ficar visível, a aba relê a chave
 * (aba congelada pelo navegador pode ter perdido o evento).
 */
function useVistoEmOutraAba(chave: string | null): string | null {
  const [visto, setVisto] = useState(() => lerVisto(chave))
  useEffect(() => {
    setVisto(lerVisto(chave))
    if (!chave) return
    const aoGravar = (e: StorageEvent) => { if (e.key === chave) setVisto(e.newValue) }
    const aoVoltar = () => { if (document.visibilityState === 'visible') setVisto(lerVisto(chave)) }
    window.addEventListener('storage', aoGravar)
    document.addEventListener('visibilitychange', aoVoltar)
    return () => {
      window.removeEventListener('storage', aoGravar)
      document.removeEventListener('visibilitychange', aoVoltar)
    }
  }, [chave])
  return visto
}

const NENHUMA_VERSAO: ReadonlySet<string> = new Set()

/**
 * Aviso de novidades do login. `visiveis` são as áreas a que a pessoa tem acesso
 * (`areasVisiveis`); `null` = ainda carregando (critério nomeado vem do backend), e o
 * aviso não abre: abrir antes esconderia item que ela deveria ver. `userId` identifica a
 * chave que coordena as abas; sem ele, cada aba segue sozinha.
 */
export function useChangelogSplash(
  lastSeen: string | null | undefined,
  visiveis: Set<string> | null,
  userId?: number | null,
) {
  const chave = userId != null ? chaveDoVisto(userId) : null
  const vistoEmOutraAba = useVistoEmOutraAba(chave)
  // Outra aba já marcou esta versão (ou uma mais nova): nada a mostrar aqui, nem a marcar.
  const jaVistoEmOutraAba = vistoEmOutraAba !== null && compararVersao(vistoEmOutraAba, APP_VERSION) >= 0
  const novas = jaVistoEmOutraAba ? [] : computeChangelogDelta(CHANGELOG, lastSeen)
  const filtradas: EntradaFiltrada[] = visiveis ? filtrarEntradas(novas, visiveis) : []
  // O que o aviso mostrou fica congelado ao abrir: critério que muda depois (o pedido de
  // análise pego, a lista que esvazia) não fecha o aviso no meio da leitura nem o faz
  // contar como "não abriu".
  // `versoes` são TODAS as versões não vistas, com ou sem item visível: o selo "nova" das
  // abas Minhas e Todas sai delas, e congela junto com a lista.
  const mostradas = useRef<{ entradas: EntradaFiltrada[]; versoes: ReadonlySet<string> } | null>(null)
  if (mostradas.current === null && filtradas.length > 0) {
    mostradas.current = { entradas: filtradas, versoes: new Set(novas.map((e) => e.version)) }
  }
  const entries = mostradas.current?.entradas ?? filtradas
  const versoesNovas = mostradas.current?.versoes ?? NENHUMA_VERSAO
  const [dismissed, setDismissed] = useState(false)
  // `jaVistoEmOutraAba` fecha também o aviso já aberto: `entries` está congelada.
  const open = entries.length > 0 && !dismissed && !jaVistoEmOutraAba
  // Havia versões novas, mas nenhum item visível — e o aviso nunca abriu: não abre e a
  // versão é marcada vista.
  const nadaVisivel = visiveis !== null && novas.length > 0 && entries.length === 0
  const marcouSemAviso = useRef(false)
  useEffect(() => {
    if (!nadaVisivel || marcouSemAviso.current) return
    marcouSemAviso.current = true
    // Sem medida: o aviso não abriu, não há duração a medir, e 0 seria dado inventado.
    authApi.markChangelogSeen(APP_VERSION).catch(() => { /* best-effort */ })
    gravarVisto(chave, APP_VERSION)
  }, [nadaVisivel])
  // Instante em que o aviso passou a estar aberto (medição do tempo até fechar).
  const abertoEm = useRef<number | null>(null)
  useEffect(() => {
    if (open && abertoEm.current === null) abertoEm.current = Date.now()
  }, [open])
  const dismiss = () => {
    setDismissed(true)
    // Sem instante de abertura não há o que medir: marca sem medida, nunca com 0 inventado.
    const medida = abertoEm.current === null
      ? undefined
      : {
        duracao_ms: Math.min(DURACAO_MAXIMA_MS, Math.max(0, Date.now() - abertoEm.current)),
        // Só os itens visíveis: `entries` já vem filtrada.
        itens: contarItens(entries),
      }
    authApi.markChangelogSeen(APP_VERSION, medida).catch(() => { /* best-effort */ })
    gravarVisto(chave, APP_VERSION)
  }
  return { open, entries, versoesNovas, dismiss }
}
